// Cloudflare Turnstile widget — patient app (v239.13.0, C-A-75)
// Explicit-render React wrapper. Loads the Turnstile script once, renders a
// Managed-mode widget, and reports the token via onVerify. The token is
// single-use and expires after 300s; onExpire/onError clear it and the parent
// re-mounts (via a key) to issue a fresh challenge on retry.

import { useEffect, useRef } from 'react'

// Public sitekey — safe to ship in the client. Env override allows rotation
// without a code change; falls back to the configured Orella widget key.
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAADrMUigFaZoRhyPs'
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let scriptPromise = null
function loadTurnstileScript() {
  if (typeof window !== 'undefined' && window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('turnstile_script_failed')))
      if (window.turnstile) resolve()
      return
    }
    const s = document.createElement('script')
    s.src = SCRIPT_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('turnstile_script_failed'))
    document.head.appendChild(s)
  })
  return scriptPromise
}

export default function Turnstile({ onVerify, onExpire, onError, theme = 'light', action }) {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  // Keep latest callbacks in a ref so the rendered widget always calls current ones.
  const cbRef = useRef({ onVerify, onExpire, onError })
  cbRef.current = { onVerify, onExpire, onError }

  useEffect(() => {
    let cancelled = false
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          theme,
          action,
          callback: (token) => cbRef.current.onVerify && cbRef.current.onVerify(token),
          'expired-callback': () => cbRef.current.onExpire && cbRef.current.onExpire(),
          'error-callback': () => cbRef.current.onError && cbRef.current.onError(),
        })
      })
      .catch(() => { if (!cancelled && cbRef.current.onError) cbRef.current.onError() })

    return () => {
      cancelled = true
      try {
        if (widgetIdRef.current && window.turnstile) window.turnstile.remove(widgetIdRef.current)
      } catch (e) { /* widget already gone */ }
    }
  }, [theme, action])

  return <div ref={containerRef} className="cf-turnstile-container" />
}
